'use client'
import * as React from 'react'
import { Sparkles, RotateCcw } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

// Pure-browser estimator for tagging a UGC library with vision AI.
// Two-pass model: pass 1 = cheap caption/tag, pass 2 = product-match /
// quality. Token counts and per-million rates are editable so the figure
// never hardens into a stale pricing claim.
//
// Mirrors aiTaggingCost()/AI_TAGGING_DEFAULTS in
// nextapp/lib/publicToolsMath.ts (nextapp's white-label copy for Feefo, see
// this repo's CLAUDE.md "Public calculator tools").

// Editable defaults. Haiku's published rate is the starting point; the user
// can overwrite it, so nothing here is a frozen number.
const DEFAULTS = {
  library: 5_000,
  imagesPerItem: 1,
  inTokens: 1_400,
  outTokens: 200,
  inRate: 0.8, // USD per 1M input tokens (editable)
  outRate: 4.0, // USD per 1M output tokens (editable)
}

export function AiTaggingCost() {
  const [library, setLibrary] = React.useState<number>(DEFAULTS.library)
  const [imagesPerItem, setImagesPerItem] = React.useState<number>(DEFAULTS.imagesPerItem)
  const [twoPass, setTwoPass] = React.useState<boolean>(true)

  const [inTokens, setInTokens] = React.useState<number>(DEFAULTS.inTokens)
  const [outTokens, setOutTokens] = React.useState<number>(DEFAULTS.outTokens)
  const [inRate, setInRate] = React.useState<number>(DEFAULTS.inRate)
  const [outRate, setOutRate] = React.useState<number>(DEFAULTS.outRate)
  const [advancedOpen, setAdvancedOpen] = React.useState<boolean>(false)

  const images = Math.max(0, library) * Math.max(0, imagesPerItem)
  const passes = twoPass ? 2 : 1

  // Cost of a single analysis pass over every image in the library.
  const costPerPass =
    images *
    ((inTokens / 1_000_000) * inRate + (outTokens / 1_000_000) * outRate)
  const totalCost = costPerPass * passes
  const costPer1k = library > 0 ? (totalCost / library) * 1_000 : 0

  const fmt = (n: number) => {
    if (n === 0) return '$0'
    if (n < 1) return '$' + n.toFixed(2)
    if (n < 100) return '$' + n.toFixed(2)
    return '$' + Math.round(n).toLocaleString()
  }
  const num = (n: number) => Math.round(n).toLocaleString()

  function reset() {
    setLibrary(DEFAULTS.library)
    setImagesPerItem(DEFAULTS.imagesPerItem)
    setTwoPass(true)
    setInTokens(DEFAULTS.inTokens)
    setOutTokens(DEFAULTS.outTokens)
    setInRate(DEFAULTS.inRate)
    setOutRate(DEFAULTS.outRate)
  }

  const plainRead = (() => {
    if (images === 0) return 'Add a library size to see the estimate.'
    const pp = passes === 2 ? 'two passes' : 'one pass'
    return `Tagging ${num(library)} items (${num(images)} image${images === 1 ? '' : 's'}) with ${pp} of vision AI lands near ${fmt(totalCost)} as a one-off. That is about ${fmt(costPer1k)} per 1,000 items.`
  })()

  return (
    <div className="rounded-2xl border bg-card shadow-sm">
      <div className="flex items-center justify-between border-b p-5 sm:p-6">
        <div>
          <p className="text-xs font-medium uppercase tracking-[0.18em] text-primary">Vision AI tagging</p>
          <h2 className="mt-1 font-display text-xl font-medium tracking-tight sm:text-2xl">What would it cost to tag your whole UGC library?</h2>
        </div>
        <button
          type="button"
          onClick={reset}
          className="inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs text-muted-foreground transition-colors duration-200 hover:border-primary/40 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none"
        >
          <RotateCcw className="h-3 w-3" /> Reset
        </button>
      </div>

      <div className="grid gap-6 p-5 sm:p-6 md:grid-cols-2 md:gap-8 md:p-8">
        <div className="space-y-5">
          <Field label="Library size" hint="Total media items you want to tag" htmlFor="library">
            <Input
              id="library"
              type="number"
              min={0}
              step={100}
              value={library}
              onChange={(e) => setLibrary(Number(e.target.value) || 0)}
              className="tabular-nums"
            />
          </Field>

          <Field label="Images analysed per item" hint="Video items often need a few sampled frames" htmlFor="imagesPerItem">
            <Input
              id="imagesPerItem"
              type="number"
              min={1}
              step={1}
              value={imagesPerItem}
              onChange={(e) => setImagesPerItem(Math.max(1, Number(e.target.value) || 1))}
              className="tabular-nums"
            />
          </Field>

          <fieldset className="block text-sm">
            <legend className="block text-xs font-medium uppercase tracking-wider text-muted-foreground">Analysis depth</legend>
            <span className="mt-0.5 block text-xs text-muted-foreground">Pass 1 captions and tags. Pass 2 matches products and scores quality.</span>
            <div className="mt-2.5 inline-flex rounded-md border p-0.5" role="radiogroup" aria-label="Analysis depth">
              <button
                type="button"
                role="radio"
                aria-checked={!twoPass}
                onClick={() => setTwoPass(false)}
                className={cn(
                  'rounded px-3 py-1.5 text-xs font-medium transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none',
                  !twoPass ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                One pass
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={twoPass}
                onClick={() => setTwoPass(true)}
                className={cn(
                  'rounded px-3 py-1.5 text-xs font-medium transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none',
                  twoPass ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                Two pass
              </button>
            </div>
          </fieldset>

          <div className="rounded-xl border bg-secondary/20">
            <button
              type="button"
              onClick={() => setAdvancedOpen((v) => !v)}
              aria-expanded={advancedOpen}
              className="flex w-full items-center justify-between px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground transition-colors duration-200 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring motion-reduce:transition-none"
            >
              <span>Assumptions (editable estimate)</span>
              <span aria-hidden className={cn('transition-transform duration-200 motion-reduce:transition-none', advancedOpen && 'rotate-45')}>+</span>
            </button>
            {advancedOpen && (
              <div className="space-y-4 border-t px-4 py-4">
                <Field label="Input tokens per image" hint="A vision image is roughly this many tokens, by tile count" htmlFor="inTokens">
                  <Input id="inTokens" type="number" min={0} step={100} value={inTokens} onChange={(e) => setInTokens(Number(e.target.value) || 0)} className="tabular-nums" />
                </Field>
                <Field label="Output tokens per image" hint="Length of the tags or caption returned" htmlFor="outTokens">
                  <Input id="outTokens" type="number" min={0} step={50} value={outTokens} onChange={(e) => setOutTokens(Number(e.target.value) || 0)} className="tabular-nums" />
                </Field>
                <Field label="Haiku input rate (editable)" hint="USD per 1M input tokens, overwrite with the rate on the day" htmlFor="inRate">
                  <div className="relative">
                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">$</span>
                    <Input id="inRate" type="number" min={0} step={0.1} value={inRate} onChange={(e) => setInRate(Number(e.target.value) || 0)} className="pl-7 tabular-nums" />
                  </div>
                </Field>
                <Field label="Haiku output rate (editable)" hint="USD per 1M output tokens, overwrite with the rate on the day" htmlFor="outRate">
                  <div className="relative">
                    <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">$</span>
                    <Input id="outRate" type="number" min={0} step={0.1} value={outRate} onChange={(e) => setOutRate(Number(e.target.value) || 0)} className="pl-7 tabular-nums" />
                  </div>
                </Field>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-3">
          <Card label="Pass 1 cost" value={fmt(costPerPass)} sub="Caption + tag across every image" />
          <Card
            label="Pass 2 cost"
            value={twoPass ? fmt(costPerPass) : '—'}
            sub={twoPass ? 'Product match + quality score' : 'Off, single-pass estimate'}
          />
          <Card label="Cost per 1,000 items" value={fmt(costPer1k)} sub={`${num(images)} images analysed in total`} accent />
          <Card label="Total one-off cost" value={fmt(totalCost)} sub={`${passes === 2 ? 'Two passes' : 'One pass'} over ${num(library)} items`} large />
          <p className="px-1 text-sm leading-relaxed text-muted-foreground" aria-live="polite">{plainRead}</p>
        </div>
      </div>

      <div className="flex flex-col gap-3 border-t bg-secondary/30 p-5 sm:flex-row sm:items-center sm:justify-between sm:p-6">
        <p className="text-xs text-muted-foreground sm:text-sm">
          <Sparkles className="mr-1 inline h-3.5 w-3.5 text-primary" />
          Estimate only. Real cost depends on image resolution and model rates on the day. Idukki batches and caches to cut this further.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button asChild size="sm"><a href="/contact?intent=ai-tagging">Talk to us about tagging</a></Button>
          <Button asChild size="sm" variant="outline"><a href="/tools">More free tools</a></Button>
        </div>
      </div>
    </div>
  )
}

function Field({ label, hint, htmlFor, children }: { label: string; hint?: string; htmlFor: string; children: React.ReactNode }) {
  return (
    <div className="block text-sm">
      <label htmlFor={htmlFor} className="block text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</label>
      {hint && <span className="mt-0.5 block text-xs text-muted-foreground">{hint}</span>}
      <div className="mt-1.5">{children}</div>
    </div>
  )
}

function Card({ label, value, sub, accent, large }: { label: string; value: string; sub?: string; accent?: boolean; large?: boolean }) {
  return (
    <div className={cn(
      'rounded-xl border p-4',
      accent && 'border-primary/40 bg-primary/5',
      large && 'border-foreground/20 bg-foreground text-background',
    )}>
      <div className={cn('text-xs uppercase tracking-wider', large ? 'text-background/85' : 'text-muted-foreground')}>{label}</div>
      <div className="mt-1 font-display text-2xl font-medium tabular-nums md:text-3xl">{value}</div>
      {sub && <div className={cn('mt-1 text-xs', large ? 'text-background/85' : 'text-muted-foreground')}>{sub}</div>}
    </div>
  )
}
